import { useCallback, useEffect, useState } from "react";
import type { ItemRow } from "@/features/catalog/api";
import type { PaymentAccount } from "@/features/money/api";
import type { UnitOption } from "@/features/purchases/units";
import { ApiError, api } from "@/lib/api";
import { idb, STORES } from "@/lib/idb";
import type { SaleForReceipt } from "./receipt";

export interface SnapshotItem {
  variantId: string;
  itemId: string;
  nameEn: string;
  nameBn: string;
  attributes: Record<string, string>;
  sku: string;
  baseUnit: string;
  salePrice: number;
  vatRate: number;
  sdRate: number;
  trackStock: boolean;
  trackSerials?: boolean;
  searchText: string;
  onHand: number;
  barcodes: { barcode: string; unitCode: string | null }[];
  units: { unitCode: string; factor: number; salePrice: number | null }[];
}

export interface SnapshotCustomer {
  id: string;
  name: string;
  phone: string | null;
  balance: number;
  creditLimit: number | null;
  priceTierId?: string | null;
}

export interface SnapshotPriceLists {
  tiers: { id: string; nameEn: string; nameBn: string }[];
  tierPrices: Record<string, Record<string, number>>;
  reps: { id: string; name: string }[];
}

export interface Snapshot {
  tenantId: string;
  branchId: string;
  syncedAt: number;
  items: SnapshotItem[];
  customers: SnapshotCustomer[];
  accounts: PaymentAccount[];
  conversions: { fromUnit: string; toUnit: string; factor: number }[];
  /** Missing on snapshots saved before price lists existed. */
  priceLists?: SnapshotPriceLists;
  registerSessionId: string | null;
}

export interface OutboxEntry {
  clientId: string;
  tenantId: string;
  branchId: string;
  createdAt: number;
  provisional: string;
  status: "pending" | "failed";
  error?: { code: string; message: string };
  payload: Record<string, unknown>;
  receipt: SaleForReceipt;
}

const snapshotKey = (tenantId: string, branchId: string) => `${tenantId}:${branchId}`;
const SNAPSHOT_PAGE = 1000;

/** Downloads everything the POS needs to sell offline and stores it on the device (FR-OFF-01). */
export async function refreshSnapshot(
  tenantId: string,
  branchId: string,
  registerSessionId: string | null,
): Promise<Snapshot> {
  const items: SnapshotItem[] = [];
  for (let offset = 0; ; offset += SNAPSHOT_PAGE) {
    const page = await api.get<{ items: SnapshotItem[]; done: boolean }>(
      `/t/${tenantId}/pos/catalog?branchId=${branchId}&offset=${offset}&limit=${SNAPSHOT_PAGE}`,
    );
    items.push(...page.items);
    if (page.done) break;
  }
  const [{ customers }, { accounts }, meta, priceLists] = await Promise.all([
    api.get<{ customers: SnapshotCustomer[] }>(`/t/${tenantId}/pos/customers`),
    api.get<{ accounts: PaymentAccount[] }>(`/t/${tenantId}/payment-accounts`),
    api.get<{ conversions: Snapshot["conversions"] }>(`/t/${tenantId}/catalog/meta`),
    api.get<SnapshotPriceLists>(`/t/${tenantId}/pos/price-lists`),
  ]);
  const snapshot: Snapshot = {
    tenantId,
    branchId,
    syncedAt: Date.now(),
    items,
    customers,
    accounts,
    conversions: meta.conversions,
    priceLists,
    registerSessionId,
  };
  await idb.put(STORES.snapshots, snapshot, snapshotKey(tenantId, branchId));
  return snapshot;
}

export function loadSnapshot(tenantId: string, branchId: string): Promise<Snapshot | undefined> {
  return idb.get<Snapshot>(STORES.snapshots, snapshotKey(tenantId, branchId));
}

export function toItemRow(s: SnapshotItem): ItemRow {
  return {
    itemId: s.itemId,
    variantId: s.variantId,
    nameEn: s.nameEn,
    nameBn: s.nameBn,
    attributes: s.attributes,
    sku: s.sku,
    categoryId: null,
    baseUnit: s.baseUnit,
    salePrice: s.salePrice,
    mrp: null,
    lastCost: null,
    vatRate: s.vatRate,
    sdRate: s.sdRate,
    trackStock: s.trackStock,
    trackSerials: s.trackSerials ?? false,
    reorderLevel: 0,
    status: "active",
    onHand: s.onHand,
    custom: {},
  };
}

export function searchSnapshot(snapshot: Snapshot, q: string, limit = 12): ItemRow[] {
  const terms = q.normalize("NFKC").toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  const out: ItemRow[] = [];
  for (const item of snapshot.items) {
    if (terms.every((t) => item.searchText.includes(t))) {
      out.push(toItemRow(item));
      if (out.length >= limit) break;
    }
  }
  return out;
}

export function lookupSnapshot(
  snapshot: Snapshot,
  code: string,
): { item: ItemRow; unitCode: string | null } | null {
  const trimmed = code.trim();
  for (const item of snapshot.items) {
    const barcode = item.barcodes.find((b) => b.barcode === trimmed);
    if (barcode) return { item: toItemRow(item), unitCode: barcode.unitCode };
    if (item.sku === trimmed) return { item: toItemRow(item), unitCode: null };
  }
  return null;
}

export function snapshotUnits(snapshot: Snapshot, variantId: string): UnitOption[] | null {
  const item = snapshot.items.find((i) => i.variantId === variantId);
  if (!item) return null;
  const options: UnitOption[] = [{ code: item.baseUnit, factor: 1000, salePrice: null }];
  for (const u of item.units)
    options.push({ code: u.unitCode, factor: u.factor, salePrice: u.salePrice });
  for (const c of snapshot.conversions) {
    if (c.toUnit === item.baseUnit && !options.some((o) => o.code === c.fromUnit))
      options.push({ code: c.fromUnit, factor: c.factor * 1000, salePrice: null });
  }
  return options;
}

/* --------------------------------- Outbox --------------------------------- */

function deviceCode(): string {
  try {
    let code = localStorage.getItem("zeeta.device");
    if (!code) {
      code = Array.from(crypto.getRandomValues(new Uint8Array(3)), (b) =>
        b.toString(36).padStart(2, "0"),
      )
        .join("")
        .slice(0, 4)
        .toUpperCase();
      localStorage.setItem("zeeta.device", code);
    }
    return code;
  } catch {
    return "DEV";
  }
}

function nextProvisional(tenantId: string): string {
  const key = `zeeta.offline-seq.${tenantId}`;
  let seq = 1;
  try {
    seq = Number(localStorage.getItem(key) ?? "0") + 1;
    localStorage.setItem(key, String(seq));
  } catch {
    seq = Date.now() % 100_000;
  }
  return `OFF-${deviceCode()}-${String(seq).padStart(4, "0")}`;
}

const listeners = new Set<() => void>();
const notify = () => {
  for (const l of listeners) l();
};

/**
 * Stores a sale on the device until it can be synced (FR-OFF-02). The write is committed before
 * this resolves, so the cashier can hand over the receipt knowing the sale is safe.
 */
export async function queueSale(
  entry: Omit<OutboxEntry, "createdAt" | "provisional" | "status">,
): Promise<OutboxEntry> {
  const full: OutboxEntry = {
    ...entry,
    createdAt: Date.now(),
    provisional: nextProvisional(entry.tenantId),
    status: "pending",
  };
  full.receipt = { ...full.receipt, number: full.provisional };
  await idb.put(STORES.outbox, full);
  const snapshot = await loadSnapshot(entry.tenantId, entry.branchId);
  if (snapshot) {
    const lines = (entry.payload.lines ?? []) as { variantId: string; baseQty?: number }[];
    for (const line of lines) {
      const item = snapshot.items.find((i) => i.variantId === line.variantId);
      if (item && line.baseQty) item.onHand -= line.baseQty;
    }
    await idb.put(STORES.snapshots, snapshot, snapshotKey(entry.tenantId, entry.branchId));
  }
  notify();
  return full;
}

export function outboxFor(tenantId: string): Promise<OutboxEntry[]> {
  return idb
    .byIndex<OutboxEntry>(STORES.outbox, "tenant", tenantId)
    .then((rows) => rows.sort((a, b) => a.createdAt - b.createdAt));
}

let syncing: Promise<void> | null = null;

/**
 * Sends queued sales in order (FR-OFF-03). The server accepts each sale once by its client ID, so a
 * retry after a lost response cannot create a duplicate. Business rejections are kept for review.
 */
export function syncOutbox(tenantId: string): Promise<void> {
  syncing ??= (async () => {
    try {
      for (const entry of await outboxFor(tenantId)) {
        if (entry.status !== "pending") continue;
        try {
          // baseQty is only kept on the device to adjust the local stock snapshot.
          const lines = ((entry.payload.lines ?? []) as Record<string, unknown>[]).map(
            ({ baseQty: _local, ...line }) => line,
          );
          await api.post(`/t/${tenantId}/sales`, { ...entry.payload, lines });
          await idb.delete(STORES.outbox, entry.clientId);
        } catch (error) {
          if (error instanceof ApiError && error.status === 0) break;
          const failed: OutboxEntry = {
            ...entry,
            status: "failed",
            error:
              error instanceof ApiError
                ? { code: error.code, message: error.message }
                : { code: "internal_error", message: String(error) },
          };
          await idb.put(STORES.outbox, failed);
        }
        notify();
      }
    } finally {
      syncing = null;
      notify();
    }
  })();
  return syncing;
}

/** Puts a rejected sale back in the queue, e.g. after fixing the cause on the server. */
export async function retryFailed(entry: OutboxEntry): Promise<void> {
  const { error: _error, ...rest } = entry;
  await idb.put(STORES.outbox, { ...rest, status: "pending" });
  notify();
}

/** Removes a rejected sale from the device; only after a manager has dealt with it. */
export async function discardFailed(clientId: string): Promise<void> {
  await idb.delete(STORES.outbox, clientId);
  notify();
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}

/** Outbox counts plus automatic sync when the connection returns and every 30 seconds (FR-OFF-03/05). */
export function useOutbox(tenantId: string) {
  const online = useOnline();
  const [entries, setEntries] = useState<OutboxEntry[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    void outboxFor(tenantId)
      .then(setEntries)
      .catch(() => setEntries([]));
  }, [tenantId]);

  const sync = useCallback(async () => {
    setBusy(true);
    try {
      await syncOutbox(tenantId);
    } finally {
      setBusy(false);
      refresh();
    }
  }, [tenantId, refresh]);

  useEffect(() => {
    refresh();
    listeners.add(refresh);
    return () => {
      listeners.delete(refresh);
    };
  }, [refresh]);

  useEffect(() => {
    if (!online) return;
    void sync();
    const timer = setInterval(() => void sync(), 30_000);
    return () => clearInterval(timer);
  }, [online, sync]);

  return {
    online,
    syncing: busy,
    pending: entries.filter((e) => e.status === "pending"),
    failed: entries.filter((e) => e.status === "failed"),
    sync,
  };
}
